/**
 * Ice Maze — the generator (specs/word-maze-mode.md §4, §6, §7).
 *
 * Everything about *what* is on the board lives here; MazeScene only draws what this returns.
 * Phaser-free so it can be tested in node, exactly like game/pairs/deal.ts.
 */
import { MAZE } from '@/game/config';
import type { PackWord, PackClass } from '@/data/packs/types';
import { cellsOf, isDenseScript } from './segment';

/** A word this mode can hide: its icon id, its surface form, its topic, and its cells. */
export interface MazeWord {
  id: string;
  text: string;
  topic: string;
  cells: string[];
}

/** [dCol, dRow]. Only ever left-to-right or downward — see directionsFor. */
export type Dir = readonly [number, number];

export interface Placement {
  word: MazeWord;
  /** Cells the word occupies, in reading order: [col, row][]. */
  path: [number, number][];
}

export interface MazeBoard {
  round: number;
  cols: number;
  rows: number;
  /** grid[row][col] — one grapheme cluster per cell. */
  grid: string[][];
  /** The class id the board asks for, and its localized label. */
  topic: string;
  topicLabel: string;
  targets: Placement[];
  timeMs: number;
}

const RIGHT: Dir = [1, 0];
const DOWN: Dir = [0, 1];
const DOWN_RIGHT: Dir = [1, 1];
const UP_RIGHT: Dir = [1, -1];

/**
 * The directions a word may be HIDDEN in.
 *
 * Rounds 1-4: right and down. Round 5 and beyond add the two diagonals that still read left to
 * right. The reversed four are never used at any round — the mode is played by children who are
 * still assembling the left-to-right sweep, and a puzzle whose winning move is reading backwards
 * teaches against the lesson it is a reward for. grid.test.ts pins this.
 */
export function directionsFor(round: number): Dir[] {
  return round >= MAZE.diagonalsFrom
    ? [RIGHT, DOWN, DOWN_RIGHT, UP_RIGHT]
    : [RIGHT, DOWN];
}

/** Ladder lookups. The last rung repeats forever, like the Pairs ladder. */
function rung(round: number, len: number): number {
  return Math.min(Math.max(1, Math.floor(round)), len) - 1;
}
export function gridFor(round: number): [number, number] {
  return MAZE.grids[rung(round, MAZE.grids.length)];
}
export function targetsFor(round: number): number {
  return MAZE.targets[rung(round, MAZE.targets.length)];
}
export function timeForRound(round: number): number {
  return MAZE.timeMs[rung(round, MAZE.timeMs.length)];
}

/** The shortest word this language may hide: dense scripts pack a syllable per cell (§6.3). */
export function minCellsFor(lang: string): number {
  return isDenseScript(lang) ? MAZE.minCellsDense : MAZE.minCells;
}

/**
 * Words this mode can hide at all: an icon id, a topic, and a length the grid can hold. Checked as
 * DATA by data/pools/picturewords-mazefit.test.ts, so a translation pass that lengthens a pool
 * fails the build rather than quietly shrinking the boards it can deal.
 */
export function eligibleWords(words: readonly PackWord[], lang: string): MazeWord[] {
  const min = minCellsFor(lang);
  const out: MazeWord[] = [];
  for (const w of words) {
    const id = typeof w.id === 'string' ? w.id : '';
    if (!id || typeof w.text !== 'string') continue;
    const cells = cellsOf(w.text, lang);
    if (cells.length < min || cells.length > MAZE.maxCells) continue;
    out.push({ id, text: w.text, topic: typeof w.class === 'string' ? w.class : '', cells });
  }
  return out;
}

/** Unbiased Fisher-Yates. `rnd` is injectable so tests can pin an order. */
export function shuffle<T>(items: readonly T[], rnd: () => number = Math.random): T[] {
  const a = [...items];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * How often each cluster occurs in this language's own pool — the filler alphabet.
 *
 * Sampled from the pool rather than from an authored per-language alphabet, so a German grid gets
 * German letters (umlauts included), Thai gets Thai clusters and Korean gets hangul syllables that
 * actually occur — and the mode still costs no new content in any language (§6.4).
 */
export function fillerTable(words: readonly MazeWord[]): string[] {
  const table: string[] = [];
  for (const w of words) table.push(...w.cells);
  return table;
}

/** The subset of Set<string> this module needs (so callers can pass a plain Set). */
type TopicSet = { has(v: string): boolean };

export interface BuildOptions {
  round: number;
  /** The eligible pool, already ordered "learned first" by the caller (and so already this
   * language's — `eligibleWords` is where the script rules are applied). */
  pool: readonly MazeWord[];
  /** Localized topic labels, by class id. */
  classes?: readonly PackClass[];
  /** Topics already asked this run — avoided while any unused topic remains. */
  usedTopics?: TopicSet;
  rnd?: () => number;
}

/**
 * Deal one board, or null if this pool cannot fill the asked-for round at all (a language whose
 * data is too thin — the caller then ends the run rather than showing an empty grid).
 *
 * Steps, in the order §7 lists them: pick a topic that has enough eligible words, sample the
 * targets from it, place longest-first (a long word placed last usually cannot be placed at all),
 * fill from the language's own cluster frequencies, then scan every line for words the generator
 * did not mean to write and repair them.
 *
 * Everything on the board belongs to the asked-for topic. There are no planted wrong-topic words:
 * a grid is a page a child reads, and a word that is there only to be wrong makes reading it a
 * trap. What the child does with a word they read and reject is simply nothing.
 */
export function buildBoard(opts: BuildOptions): MazeBoard | null {
  const rnd = opts.rnd ?? Math.random;
  const { round, pool } = opts;
  const [cols, rows] = gridFor(round);
  const wanted = targetsFor(round);
  // A word must never have to fill a row end to end: at cols-1 there is always somewhere to start.
  const maxCells = Math.min(MAZE.maxCells, cols - 1);

  const usable = pool.filter((w) => w.cells.length <= maxCells);
  let fallback: MazeBoard | null = null;
  const byTopic = new Map<string, MazeWord[]>();
  for (const w of usable) {
    const list = byTopic.get(w.topic);
    if (list) list.push(w);
    else byTopic.set(w.topic, [w]);
  }

  const candidates = [...byTopic.entries()].filter(([, ws]) => ws.length >= wanted);
  if (candidates.length === 0) return null;
  // Prefer a topic this run has not asked for yet; fall back to any once they are used up.
  const fresh = candidates.filter(([id]) => !opts.usedTopics?.has(id));
  const [topic, topicWords] = shuffle(fresh.length > 0 ? fresh : candidates, rnd)[0];
  const label = opts.classes?.find((c) => c.id === topic)?.label ?? topic;

  const finish = (placed: Placement[]): MazeBoard => {
    const grid = blankGrid(cols, rows);
    for (const p of placed) {
      p.path.forEach(([c, r], i) => { grid[r][c] = p.word.cells[i]; });
    }
    fillBlanks(grid, fillerTable(usable), rnd);
    repairAccidentals(grid, placed, usable, rnd);
    return {
      round,
      cols,
      rows,
      grid,
      topic,
      topicLabel: label,
      targets: placed,
      timeMs: timeForRound(round),
    };
  };

  for (let attempt = 0; attempt < MAZE.dealAttempts; attempt++) {
    // Sampled, never taken off the top: the learned-first ordering only changes when mastery does,
    // which is days apart, so "the first four" would deal the same board for a week.
    const targets = shuffle(topicWords, rnd).slice(0, wanted);
    const placed = placeAll(targets, cols, rows, round, rnd);
    if (!placed) continue;
    const board = finish(placed);
    // The repair pass can always break a word the FILLERS wrote, but not one that two crossing
    // placements happen to spell between them — every cell there is a letter some word needs. That
    // board is simply re-dealt: cheaper than teaching the repair to move a placed word.
    if (accidentalWords(board, usable).length === 0) return board;
    fallback = board;
  }

  // Every attempt failed to place (a very unlucky draw, or a very cramped language). Fall back to
  // the shortest words in the topic, which always fit — a treat must never fail to deal.
  if (fallback) return fallback;
  const shortest = [...topicWords].sort((a, b) => a.cells.length - b.cells.length).slice(0, wanted);
  const placed = placeAll(shortest, cols, rows, round, rnd);
  return placed ? finish(placed) : null;
}

function blankGrid(cols: number, rows: number): string[][] {
  return Array.from({ length: rows }, () => Array.from({ length: cols }, () => ''));
}

/**
 * Place every word, longest first, or return null. Words may CROSS — but only on a cell that
 * already holds the same cluster, which is what makes a small grid dealable without lying about
 * what is written in it.
 */
function placeAll(
  targets: readonly MazeWord[], cols: number, rows: number, round: number, rnd: () => number,
): Placement[] | null {
  const order = [...targets].sort((a, b) => b.cells.length - a.cells.length);
  const grid = blankGrid(cols, rows);
  const out: Placement[] = [];
  const dirs = directionsFor(round);

  for (const w of order) {
    const path = findSpot(grid, w, dirs, cols, rows, rnd);
    if (!path) return null;
    path.forEach(([c, r], i) => { grid[r][c] = w.cells[i]; });
    out.push({ word: w, path });
  }
  return out;
}

function findSpot(
  grid: string[][], w: MazeWord, dirs: Dir[], cols: number, rows: number, rnd: () => number,
): [number, number][] | null {
  const starts: [number, number][] = [];
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) starts.push([c, r]);
  for (const dir of shuffle(dirs, rnd)) {
    for (const [c0, r0] of shuffle(starts, rnd)) {
      const path: [number, number][] = [];
      let ok = true;
      for (let i = 0; i < w.cells.length; i++) {
        const c = c0 + dir[0] * i;
        const r = r0 + dir[1] * i;
        if (c < 0 || c >= cols || r < 0 || r >= rows) { ok = false; break; }
        const held = grid[r][c];
        if (held !== '' && held !== w.cells[i]) { ok = false; break; }
        path.push([c, r]);
      }
      if (ok) return path;
    }
  }
  return null;
}

function fillBlanks(grid: string[][], table: readonly string[], rnd: () => number): void {
  if (table.length === 0) return;
  for (const row of grid) {
    for (let c = 0; c < row.length; c++) {
      if (row[c] === '') row[c] = table[Math.floor(rnd() * table.length)];
    }
  }
}

/** Every straight line of the grid, in the four directions a player can select along. */
function linesOf(grid: string[][]): [number, number][][] {
  const rows = grid.length;
  const cols = grid[0]?.length ?? 0;
  const dirs: Dir[] = [RIGHT, DOWN, DOWN_RIGHT, UP_RIGHT];
  const lines: [number, number][][] = [];
  for (const [dc, dr] of dirs) {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        // Only start a line where the previous cell in this direction is off-grid.
        const pc = c - dc;
        const pr = r - dr;
        if (pc >= 0 && pc < cols && pr >= 0 && pr < rows) continue;
        const line: [number, number][] = [];
        let cc = c;
        let rr = r;
        while (cc >= 0 && cc < cols && rr >= 0 && rr < rows) {
          line.push([cc, rr]);
          cc += dc;
          rr += dr;
        }
        if (line.length > 1) lines.push(line);
      }
    }
  }
  return lines;
}

/** Cell-path key, so an intended placement can be compared against a run found in the grid. */
function pathKey(path: readonly [number, number][]): string {
  return path.map(([c, r]) => `${c},${r}`).join('|');
}

/**
 * Is this run of cells part of a word the generator deliberately wrote — the whole placement, or a
 * stretch inside it?
 *
 * The sub-run case is not hypothetical and cannot be designed away: English hides `monkey`, which
 * contains `key`; German hides `Schmetterling`. Those are words the grid genuinely spells, and
 * re-rolling a cell to break them would corrupt the word that was placed. Selecting one resolves
 * to nothing and therefore costs nothing (maze/select.ts), which keeps §5.3's promise. What the
 * repair pass is really for is FILLERS writing a word by accident — those always have a filler
 * cell to re-roll.
 */
function insidePlacement(slice: readonly [number, number][], placed: readonly Placement[]): boolean {
  const key = pathKey(slice);
  const rkey = pathKey([...slice].reverse());
  return placed.some((p) => {
    // Bracketed so a slice is matched cell-for-cell: without the bars, "0,0|11,0" is a substring
    // of "10,0|11,0" and a run through two unrelated cells would look like part of a word.
    const full = `|${pathKey(p.path)}|`;
    return full.includes(`|${key}|`) || full.includes(`|${rkey}|`);
  });
}

/**
 * Rub out words the generator did not mean to write.
 *
 * §5.3 promises that selecting letters that spell nothing costs nothing — which is only honest if
 * the fillers never accidentally spell a real word of the pool. Any such occurrence is broken by
 * re-rolling ONE of its filler cells (a cell no placement uses); such a cell always exists, since
 * an occurrence made only of placed cells would have to be a placement itself.
 */
function repairAccidentals(
  grid: string[][], placed: readonly Placement[], pool: readonly MazeWord[], rnd: () => number,
): void {
  const table = fillerTable(pool);
  const owned = new Set(placed.flatMap((p) => p.path.map(([c, r]) => `${c},${r}`)));
  const byLength = new Map<number, Set<string>>();
  for (const w of pool) {
    const key = w.cells.join(' ');
    const set = byLength.get(w.cells.length);
    if (set) set.add(key);
    else byLength.set(w.cells.length, new Set([key]));
  }
  const lengths = [...byLength.keys()].sort((a, b) => a - b);

  // Bounded: each pass repairs at least one occurrence, and the grid is at most 99 cells.
  for (let pass = 0; pass < 40; pass++) {
    let repaired = false;
    for (const line of linesOf(grid)) {
      for (const len of lengths) {
        for (let i = 0; i + len <= line.length; i++) {
          const slice = line.slice(i, i + len);
          if (!byLength.get(len)!.has(slice.map(([c, r]) => grid[r][c]).join(' '))) continue;
          if (insidePlacement(slice, placed)) continue;
          const free = slice.filter(([c, r]) => !owned.has(`${c},${r}`));
          if (free.length === 0) continue;
          const [c, r] = free[Math.floor(rnd() * free.length)];
          const before = grid[r][c];
          for (let t = 0; t < 8 && grid[r][c] === before; t++) {
            grid[r][c] = table[Math.floor(rnd() * table.length)];
          }
          repaired = true;
        }
      }
    }
    if (!repaired) return;
  }
}

/** The unintended pool words a finished board still contains — for tests and the smoke run. */
export function accidentalWords(board: MazeBoard, pool: readonly MazeWord[]): string[] {
  const placed = board.targets;
  const byKey = new Map<string, string>();
  for (const w of pool) byKey.set(w.cells.join(' '), w.text);
  const found: string[] = [];
  for (const line of linesOf(board.grid)) {
    for (let len = 2; len <= MAZE.maxCells; len++) {
      for (let i = 0; i + len <= line.length; i++) {
        const slice = line.slice(i, i + len);
        const text = byKey.get(slice.map(([c, r]) => board.grid[r][c]).join(' '));
        if (!text) continue;
        if (insidePlacement(slice, placed)) continue;
        found.push(text);
      }
    }
  }
  return found;
}
